import { useNavigate } from '@tanstack/react-router'
import { useEffect, type ReactNode } from 'react'

import { EmptyState, Spinner } from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'

/**
 * Route guard.
 *
 * A component rather than a router `beforeLoad`, because the answer comes from
 * the server and is therefore asynchronous and cached in React Query. Putting
 * it in `beforeLoad` would mean either duplicating that fetch or blocking every
 * navigation on it.
 *
 * This is navigation, not access control. It decides what to render, and it
 * runs in the browser where anyone can change it. Every protected thing is
 * protected by the API refusing to answer — see ADR-0012 and ADR-0013.
 */
export const RequireSession = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const { isAuthenticated, isLoading, session } = useAuth()
  const navigate = useNavigate()

  /**
   * Somebody holding a password an administrator generated goes to the screen
   * that fixes it, not to a shell where every request answers 403.
   *
   * Navigation, like the guard above it. The obligation itself is enforced by
   * the API, which refuses every permission-checked route with
   * `password_change_required` until the password is changed — so defeating
   * this in a browser console reveals an empty shell and nothing else.
   */
  const mustChangePassword = session?.user.mustChangePassword ?? false

  useEffect(() => {
    if (isLoading) return

    if (!isAuthenticated) {
      void navigate({ to: '/login', replace: true })
      return
    }

    if (mustChangePassword) {
      void navigate({ to: '/trocar-senha', replace: true })
    }
  }, [isAuthenticated, isLoading, mustChangePassword, navigate])

  if (isLoading) {
    return (
      <div
        className="flex h-[100dvh] items-center justify-center bg-(--surface-base)"
        role="status"
        aria-live="polite"
      >
        <Spinner />
        <span className="sr-only">Verificando sessão…</span>
      </div>
    )
  }

  // The redirect above is in flight; rendering the shell for an instant would
  // flash chrome at someone who is not signed in — or at someone who is about
  // to be sent to change their password.
  if (!isAuthenticated || mustChangePassword) return null

  return children
}

/**
 * O console da plataforma (F119).
 *
 * Navegação, como os guardas acima, e pela mesma razão: a API recusa
 * `/api/v1/platform/*` com `platform_admin_required` por conta própria, então
 * quem desligar isto no console do navegador encontra uma tela que não carrega
 * nada. O que este componente evita é oferecer um caminho que sempre falharia.
 *
 * Uma `EmptyState` em vez de um redirecionamento: mandar para o Início alguém
 * que digitou `/plataforma` esconderia a razão, e a pessoa tentaria de novo.
 */
export const RequirePlatformAdmin = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const { isPlatformAdmin, isLoading } = useAuth()

  if (isLoading) return null

  if (!isPlatformAdmin) {
    return (
      <EmptyState
        title="Esta área é da operação da plataforma"
        description="Só quem administra esta instalação do Aeris One abre as contas dos lojistas."
      />
    )
  }

  return children
}

/**
 * The inverse, for the login screen.
 *
 * Without it, a signed-in user who navigates back to /login sees a form that
 * would sign them in again — harmless but confusing, and it makes the browser
 * Back button behave oddly after signing in.
 */
export const RedirectWhenSignedIn = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const { isAuthenticated, isLoading } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      void navigate({ to: '/inicio', replace: true })
    }
  }, [isAuthenticated, isLoading, navigate])

  return children
}
